import { useEffect, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  DETAIL_PANE_MAX_WIDTH,
  SettingsMenuView,
  resolveTheme,
  useBreakpoint,
  useMasterDetailSlots,
  type SettingsMenuSection,
  type UiTheme,
} from "@heraion/ui";
import { PANE_FLEX } from "./shell/appShellStyles";
import { useThemePalette } from "./theme";
import { CatalogScreen } from "./CatalogScreen";
import { PreferencesScreen } from "./PreferencesScreen";
import { BodyScreen } from "./BodyScreen";
import { PainMapScreen } from "./PainMapScreen";
import { AccountScreen } from "./AccountScreen";
import { CoachSettingsScreen } from "./CoachSettingsScreen";

// Web-rnw Settings surface — the console mirror of mobile's Settings tab,
// SAME IA (Settings → sub-screen, never a top-level tab per lift). The menu
// itself is the shared SettingsMenuView; only the navigation differs by
// viewport (the lift seam):
//   wide   → master-detail (menu list pane left, the selected sub-screen
//            fills the right pane; the active row stays highlighted).
//   narrow → menu → in-place sub-screen with a back row (state push), the
//            phone experience.
// Settings is app config only. MAXes / Effort / Timers / Measurements moved to
// the Me avatar-menu (ME-1 / MS-1) — they are the athlete's own data, not app
// config, mirroring the mobile Me tab. Account / sign-out reach from here and
// from the avatar-menu.

const LIST_PANE_WIDTH = 232;

export type SettingsSubKey = "account" | "catalog" | "body" | "painMap" | "prefs" | "coach";
type SubKey = SettingsSubKey;

export function SettingsScreen({
  requestedSub = null,
  onRequestedSubHandled,
}: {
  /** Cross-tab deep-link (e.g. the pain map from the Home card). The
   *  shell owns the tab switch; this only opens the sub-screen once it's
   *  here, mirroring AppShell's composerRequest consume-then-clear pattern. */
  requestedSub?: SettingsSubKey | null;
  onRequestedSubHandled?: () => void;
} = {}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const [sub, setSub] = useState<SubKey | null>(null);
  const styles = makeStyles(theme);

  useEffect(() => {
    if (requestedSub === null) return;
    setSub(requestedSub);
    onRequestedSubHandled?.();
  }, [requestedSub, onRequestedSubHandled]);

  const sections: SettingsMenuSection[] = [
    {
      label: t("mobile.settings.sectionAccount", { defaultValue: "Account" }),
      rows: [
        {
          key: "account",
          title: t("mobile.account.title", { defaultValue: "Account" }),
          sub: t("mobile.account.navSub", {
            defaultValue: "Name, password, export, delete, sign out",
          }),
        },
      ],
    },
    {
      // MAXes / Effort / Timers moved to Me (ME-1). The catalog stays until
      // ME-4 moves it to the Admin experience (it is a shared library).
      label: t("mobile.settings.sectionTraining", { defaultValue: "Training" }),
      rows: [
        {
          key: "catalog",
          title: t("mobile.catalog.title", { defaultValue: "Catalog" }),
          sub: t("mobile.profile.navCatalogSub", {
            defaultValue: "Browse exercises, records and history.",
          }),
        },
      ],
    },
    {
      // Measurements moved to Me -> Corpo (MS-1). Sex / age / figure stay
      // here; the pain map has its own row, as on mobile (inbox 63d06cce).
      label: t("mobile.settings.sectionBody", { defaultValue: "Body" }),
      rows: [
        {
          key: "body",
          title: t("mobile.body.navTitle", { defaultValue: "Body" }),
          sub: t("mobile.body.navSub", { defaultValue: "Sex, age, figure" }),
        },
        {
          key: "painMap",
          title: t("bodymap.painTitle"),
          sub: t("mobile.body.navPainMapSub"),
        },
      ],
    },
    {
      label: t("mobile.settings.sectionPrefs", { defaultValue: "Preferences" }),
      rows: [
        {
          key: "prefs",
          title: t("mobile.prefs.title", { defaultValue: "Units & display" }),
          sub: t("mobile.prefs.navSub", {
            defaultValue: "Units, language, hints",
          }),
        },
      ],
    },
    {
      label: t("coach.title", { defaultValue: "Coach & athletes" }),
      rows: [
        {
          key: "coach",
          title: t("coach.title", { defaultValue: "Coach & athletes" }),
          sub: t("coach.subtitle"),
        },
      ],
    },
  ];

  const menu = (selectedKey: string | null, dense = false) => (
    <SettingsMenuView
      theme={theme}
      sections={sections}
      selectedKey={selectedKey}
      onSelect={(key) => setSub(key as SubKey)}
      dense={dense}
    />
  );

  const detail = (key: SubKey) => {
    switch (key) {
      case "account":
        return <AccountScreen />;
      case "prefs":
        return <PreferencesScreen />;
      case "body":
        return <BodyScreen />;
      case "painMap":
        return <PainMapScreen />;
      case "catalog":
        return <CatalogScreen />;
      case "coach":
        return <CoachSettingsScreen />;
    }
  };

  // MX-8's twin: ONE tree shape for both layouts, so `detail(sub)` (a form
  // screen the athlete may be mid-edit in) stays the same node across a
  // resize instead of being torn down by two `if (wide) return` branches that
  // put it at different positions. `sub`'s own screen only ever (re)mounts
  // when `sub` itself changes, never when `wide` does. See
  // useMasterDetailSlots for the shared three-slot shape.
  const { showList: showMenu, showBack, showDetail } = useMasterDetailSlots(
    wide,
    sub !== null,
  );
  return (
    <View style={wide ? styles.row : styles.flex}>
      {showMenu ? (
        <View style={wide ? [styles.listPane, styles.listBody] : styles.flex}>
          {wide ? (
            menu(sub, true)
          ) : (
            <ScrollView style={styles.flex} contentContainerStyle={styles.menuBody}>
              {menu(null)}
            </ScrollView>
          )}
        </View>
      ) : null}
      {showBack ? (
        <Pressable
          accessibilityRole="button"
          hitSlop={8}
          style={({ pressed }) => [styles.backRow, pressed && styles.rowPressed]}
          onPress={() => setSub(null)}
        >
          <Text style={styles.back}>
            ‹ {t("mobile.common.back", { defaultValue: "Back" })}
          </Text>
        </Pressable>
      ) : null}
      {showDetail ? (
        <View style={wide ? styles.detailPane : styles.flex}>
          {sub ? (
            // Catalog is itself master-detail → fill the pane, no extra
            // wrapper in EITHER layout (it manages its own internal scroll;
            // an ancestor flex box around it is one more place that height
            // chain can break). The single-column form surfaces get a
            // wrapper so its cap can toggle without changing the node: capped
            // on wide, the neutral PANE_FLEX (no cap) on narrow.
            sub === "catalog" ? (
              detail(sub)
            ) : (
              <View style={wide ? styles.formCap : PANE_FLEX}>{detail(sub)}</View>
            )
          ) : (
            <View style={styles.empty}>
              <Text style={styles.emptyGlyph}>‹</Text>
              <Text style={styles.emptyTitle}>
                {t("mobile.settings.selectPrompt", {
                  defaultValue: "Select a setting to open it.",
                })}
              </Text>
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    row: { flex: 1, flexDirection: "row" },
    // Secondary, in-content nav — sits on the page bg (NOT surface) so it
    // reads as subordinate to the app rail, never a twin chrome panel.
    listPane: {
      width: LIST_PANE_WIDTH,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: LIST_PANE_WIDTH,
      borderRightWidth: 1,
      borderRightColor: p.border,
      backgroundColor: p.bg,
    },
    listBody: { paddingHorizontal: s.s5, paddingVertical: s.s4 },
    menuBody: { paddingHorizontal: s.s5, paddingVertical: s.s4 },
    detailPane: { flex: 1, minWidth: 0 },
    // Single-column forms: capped width, left-aligned in the detail pane.
    // Its uncapped twin is the shared PANE_FLEX (appShellStyles.ts).
    formCap: { flex: 1, width: "100%", maxWidth: DETAIL_PANE_MAX_WIDTH },
    empty: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
      gap: s.s3,
    },
    emptyGlyph: {
      color: p.ink3,
      fontSize: 44,
      lineHeight: 44,
      fontWeight: "300",
    },
    emptyTitle: {
      color: p.ink2,
      fontSize: t.sizes.lg,
      fontWeight: "600",
      textAlign: "center",
      maxWidth: 280,
    },
    backRow: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
    },
    rowPressed: { backgroundColor: p.surface2 },
    back: { color: p.brand500, fontSize: t.sizes.base },
  });
}
